import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditService, Audit, AuditTemplate } from '@/firebase/auditService';
import { locationService, Location } from '@/firebase/locationService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';

interface CreateAuditInstanceModalProps {
  auditTemplate: AuditTemplate;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateAuditInstanceModal: React.FC<CreateAuditInstanceModalProps> = ({ 
  auditTemplate, 
  onClose,
  onSuccess 
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      loadLocations();
    }
  }, [currentOrganization?.id]);

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;

    try {
      const result = await locationService.getAllLocations(currentOrganization.id);
      if (result.success && result.data) {
        setLocations(result.data.filter(loc => !loc.deleted && loc.id));
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const calculateNextAuditDate = (dueDate: Date, frequency: 'yearly' | 'semi-yearly' | 'quarterly'): Date | null => {
    const nextDate = new Date(dueDate);
    switch (frequency) {
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
      case 'semi-yearly':
        nextDate.setMonth(nextDate.getMonth() + 6);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      default:
        return null;
    }
    return nextDate;
  };

  const handleCreateAudit = async () => {
    if (!currentOrganization?.id || !selectedLocation || !scheduledDate || !dueDate) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    const location = locations.find(l => l.id === selectedLocation);

    if (!location) {
      showNotification('Invalid location selected', 'error');
      return;
    }

    setIsCreating(true);
    try {
      const auditData: Omit<Audit, 'id' | 'createdAt' | 'updatedAt'> = {
        auditTemplateId: auditTemplate.id!,
        auditTemplateName: auditTemplate.name,
        locationId: location.id!,
        locationName: location.name,
        organizationId: currentOrganization.id,
        scheduledDate: Timestamp.fromDate(new Date(scheduledDate)),
        dueDate: Timestamp.fromDate(new Date(dueDate)),
        frequency: auditTemplate.frequency,
        status: 'planned',
        complianceTemplateIds: auditTemplate.complianceTemplateIds || [],
        associatedTaskIds: [],
        completionPercentage: 0,
      };

      // Calculate next audit date based on frequency
      const nextAuditDate = calculateNextAuditDate(new Date(dueDate), auditTemplate.frequency);
      if (nextAuditDate) {
        auditData.nextAuditDate = Timestamp.fromDate(nextAuditDate);
      }

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.createAuditInstance(auditData, userInfo);

      if (result.success) {
        showNotification('Audit created successfully', 'success');
        onClose();
        if (onSuccess) onSuccess();
        // Navigate to audit execution view if ID is available
        if (result.id) {
          navigate(`/audit/${result.id}`);
        }
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error creating audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">Run Audit</h2>
              <p className="text-sm text-gray-600 mt-1">{auditTemplate.name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {/* Location Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select a location...</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Scheduled Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Date *
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                required
                min={scheduledDate}
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be after scheduled date
              </p>
            </div>

            {/* Info about frequency */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Frequency:</strong> {auditTemplate.frequency}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Next audit will be automatically scheduled based on this frequency after completion.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateAudit}
              disabled={isCreating || !selectedLocation || !scheduledDate || !dueDate}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Audit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAuditInstanceModal;

